import { Link } from "react-router-dom";
import { BalancePending } from "@/components/PendingTag";
import { Spinner } from "@/components/ui/Feedback";
import { Panel } from "@/components/ui/Layout";
import { Money } from "@/components/ui/Money";
import { useAccounts, useTransactions } from "@/lib/queries";
import { TransactionRow } from "../transactions/TransactionRow";

/**
 * Stands in for the dashboard when it can't be loaded. The dashboard's totals (income, spending,
 * net worth, budgets) are computed by the server, so they genuinely need a connection; what the
 * device does hold - account balances and recent transactions - is shown instead of an error.
 */
export function OfflineOverview() {
  const accounts = useAccounts();
  const recent = useTransactions({});

  if (accounts.isPending || recent.isPending) return <Spinner />;

  const rows = recent.data?.pages.flatMap((p) => p.data).slice(0, 8) ?? [];
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-3">
        Dashboard totals, budgets and reports need a connection. Here's what's saved on this device.
      </p>

      <Panel title="Accounts" bodyClassName="p-0">
        {accounts.data?.length ? (
          <ul>
            {accounts.data.map((a) => (
              <li key={a.id} className="border-t border-line first:border-t-0">
                <Link to={`/transactions?account_id=${a.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-2">
                  <span className="min-w-0 truncate text-sm font-medium text-ink">{a.name}</span>
                  <span className="flex items-center gap-2">
                    <BalancePending count={a.pending_count} />
                    <Money value={a.balance} currency={a.currency} className="text-sm font-medium" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-6 text-sm text-ink-3">No accounts yet.</p>
        )}
      </Panel>

      <Panel title="Recent transactions" bodyClassName="p-0">
        {rows.length ? (
          <ul>{rows.map((t) => <TransactionRow key={t.id} txn={t} />)}</ul>
        ) : (
          <p className="px-4 py-6 text-sm text-ink-3">No transactions yet.</p>
        )}
      </Panel>
    </div>
  );
}
